import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_ROUTES, getCupsForPhase, type TournamentDetail } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { playerStats, teamStats, type StatsEvents } from '@/lib/zambepong-stats'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { TeamStatsCard } from './TeamStatsCard'
import { PlayerStatsCard } from './PlayerStatsCard'

type StatsPanelProps = { detail: TournamentDetail; tournamentId: string; version: number }

const EMPTY_EVENTS: StatsEvents = { cupEvents: [], airballs: [] }

/** Tab statistiche: carica gli eventi bicchiere/airball al montaggio e a ogni messaggio live (`version`),
 * poi lascia scegliere squadra e formato (3/6 bicchieri, di default 6 se esistono partite a 6). */
export function StatsPanel({ detail, tournamentId, version }: StatsPanelProps) {
  const { t } = useTranslation()
  const [events, setEvents] = useState<StatsEvents>(EMPTY_EVENTS)
  // Solo il primo caricamento mostra "caricamento…": le riletture live tengono a video i dati vecchi.
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(detail.teams[0]?.id)

  const settings = detail.tournament
  const has3Format = detail.matches.some((m) => getCupsForPhase(m.phase, settings) === 3)
  const has6Format = detail.matches.some((m) => getCupsForPhase(m.phase, settings) === 6)
  const [cupCount, setCupCount] = useState<3 | 6>(has6Format ? 6 : 3)

  useEffect(() => {
    const controller = new AbortController()
    apiFetch<StatsEvents>(`${API_ROUTES.tournaments}/${tournamentId}/events`, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return
        setEvents(data)
        setIsLoading(false)
      })
      // Errore di rete: restano gli ultimi dati; li aggiorna il prossimo messaggio live.
      .catch(() => { if (!controller.signal.aborted) setIsLoading(false) })
    return () => controller.abort()
  }, [tournamentId, version])

  if (detail.teams.length === 0) return <p className="font-body text-sm text-ink-soft">{t('zambePong.stats.empty')}</p>
  if (isLoading) return <p className="font-body text-sm text-ink-soft">{t('zambePong.stats.loading')}</p>

  const selectedTeam = detail.teams.find((team) => team.id === selectedTeamId) ?? detail.teams[0]
  const teams = teamStats(detail, events)
  const selectedTeamStats = teams.find((s) => s.teamId === selectedTeam.id)
  const players = playerStats(detail, events, cupCount)

  return (
    <div className="space-y-6" data-testid="stats-panel">
      <SegmentedTabs
        tabs={detail.teams.map((team) => ({ key: team.id, label: team.name }))}
        active={selectedTeam.id}
        onSelect={setSelectedTeamId}
        ariaLabel={t('zambePong.stats.teamSelector')}
        idPrefix="stats-team"
        panelId="stats-team-panel"
        testIdPrefix="stats-team-"
      />

      {selectedTeamStats && (
        <div role="tabpanel" id="stats-team-panel" aria-labelledby={`stats-team-${selectedTeam.id}`}>
          <TeamStatsCard stats={selectedTeamStats} teamName={selectedTeam.name} />
        </div>
      )}

      {has3Format && has6Format && (
        <SegmentedTabs
          tabs={(['3', '6'] as const).map((count) => ({ key: count, label: t(`zambePong.stats.format${count}`) }))}
          active={String(cupCount) as '3' | '6'}
          onSelect={(count) => setCupCount(Number(count) as 3 | 6)}
          ariaLabel={t('zambePong.stats.formatSelector')}
          idPrefix="stats-format"
          panelId="stats-players"
          testIdPrefix="stats-format-"
          fill
          className="max-w-md"
        />
      )}

      <div id="stats-players" className="grid gap-4 md:grid-cols-2">
        {selectedTeam.players.map((player) => {
          const stats = players.find((p) => p.playerId === player.id)
          if (!stats) return null
          return <PlayerStatsCard key={player.id} player={player} stats={stats} cupCount={cupCount} />
        })}
      </div>
    </div>
  )
}
